import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { getApiBaseUrl } from "../../services/getData";
import { ANKETA_SECTIONS, pick, renderField } from "./tabs/PatientAnketa";
import "./tabs/PatientAnketa.css";
import "./AnketaPublic.css";
import LogoText from "../../media/logo_text.webp";
import {
  IoPersonOutline,
  IoChatbubblesOutline,
  IoWarningOutline,
  IoPulseOutline,
  IoHeartOutline,
  IoBusinessOutline,
  IoMedkitOutline,
  IoShieldCheckmarkOutline,
  IoPeopleOutline,
  IoBodyOutline,
  IoAnalyticsOutline,
  IoWomanOutline,
  IoManOutline,
  IoRestaurantOutline,
  IoLeafOutline,
  IoHappyOutline,
  IoShieldOutline,
  IoSearchOutline,
  IoFitnessOutline,
  IoDocumentTextOutline,
  IoCheckmarkCircleOutline,
  IoCheckmarkOutline,
  IoPaperPlaneOutline,
  IoGlobeOutline,
} from "react-icons/io5";

const L = (ru, en) => ({ ru, en });

const SECTION_ICONS = {
  personal: <IoPersonOutline />,
  reason: <IoChatbubblesOutline />,
  urgent: <IoWarningOutline />,
  hpi: <IoPulseOutline />,
  chronic: <IoHeartOutline />,
  hospital: <IoBusinessOutline />,
  medications: <IoMedkitOutline />,
  allergies: <IoShieldCheckmarkOutline />,
  family: <IoPeopleOutline />,
  ros: <IoBodyOutline />,
  endocrine: <IoAnalyticsOutline />,
  female: <IoWomanOutline />,
  male: <IoManOutline />,
  nutrition: <IoRestaurantOutline />,
  lifestyle: <IoLeafOutline />,
  psych: <IoHappyOutline />,
  infections: <IoShieldOutline />,
  screenings: <IoSearchOutline />,
  measurements: <IoFitnessOutline />,
  documents: <IoDocumentTextOutline />,
  confirmation: <IoCheckmarkCircleOutline />,
};

const makeHandlers = (setValues) => ({
  setVal: (id, val) => setValues((prev) => ({ ...prev, [id]: val })),
  toggleCheck: (id, key) =>
    setValues((prev) => {
      const cur = Array.isArray(prev[id]) ? prev[id] : [];
      return {
        ...prev,
        [id]: cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key],
      };
    }),
  setPair: (id, key, val) =>
    setValues((prev) => {
      const cur = prev[id] && typeof prev[id] === "object" ? prev[id] : {};
      return { ...prev, [id]: { ...cur, [key]: val } };
    }),
  setCell: (id, row, col, val) =>
    setValues((prev) => {
      const rows = Array.isArray(prev[id])
        ? prev[id].map((r) => ({ ...r }))
        : [];
      while (rows.length <= row) rows.push({});
      rows[row] = { ...rows[row], [col]: val };
      return { ...prev, [id]: rows };
    }),
});

function AnketaPublic() {
  const { token } = useParams();

  const [state, setState] = useState("loading"); // loading | active | filled | invalid
  const [lang, setLang] = useState("ru");
  const [values, setValues] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handlers = useMemo(() => makeHandlers(setValues), []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const url = `${getApiBaseUrl()}anketa/${token}`;
        const response = await axios.get(url, { withCredentials: true });
        const info = response?.data?.request ?? {};
        if (cancelled) return;
        if (info.status === "invalid") {
          setState("invalid");
        } else if (info.filled || info.status === "filled") {
          setState("filled");
        } else {
          setState("active");
        }
      } catch (_) {
        if (!cancelled) setState("invalid");
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleSubmit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const url = `${getApiBaseUrl()}anketa/${token}`;
      const response = await axios.post(url, values, {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      });
      const result = response?.data?.request ?? {};
      if (result.status === "success") {
        setState("filled");
      } else {
        setError(
          result.message ||
            pick(
              L(
                "Не удалось сохранить анкету",
                "Failed to save the questionnaire",
              ),
              lang,
            ),
        );
      }
    } catch (_) {
      setError(
        pick(
          L("Не удалось сохранить анкету", "Failed to save the questionnaire"),
          lang,
        ),
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (state === "loading") {
    return (
      <div className="anketa-public-shell">
        <div className="anketa-public-status">
          {pick(L("Загрузка…", "Loading…"), lang)}
        </div>
      </div>
    );
  }

  if (state === "invalid") {
    return (
      <div className="anketa-public-shell">
        <div className="anketa-public-card">
          <div className="anketa-public-brand">
            <img src={LogoText} alt="REFRESH Clinic" />
          </div>
          <h1 className="anketa-public-title">
            {pick(L("Ссылка недействительна", "Invalid link"), lang)}
          </h1>
          <p className="anketa-public-message">
            {pick(
              L(
                "Эта ссылка на анкету недействительна или была отозвана. Обратитесь в клинику за новой ссылкой.",
                "This questionnaire link is invalid or has been revoked. Please contact the clinic for a new link.",
              ),
              lang,
            )}
          </p>
        </div>
      </div>
    );
  }

  if (state === "filled") {
    return (
      <div className="anketa-public-shell">
        <div className="anketa-public-card">
          <div className="anketa-public-brand">
            <img src={LogoText} alt="REFRESH Clinic" />
          </div>
          <div className="anketa-public-check">
            <IoCheckmarkOutline />
          </div>
          <h1 className="anketa-public-title">
            {pick(L("Анкета заполнена", "Questionnaire completed"), lang)}
          </h1>
          <p className="anketa-public-message">
            {pick(
              L(
                "Спасибо! Ваши ответы отправлены врачу. Если вам нужно что-то уточнить или изменить — обратитесь в клинику.",
                "Thank you! Your answers have been sent to the doctor. If you need to clarify or change anything, please contact the clinic.",
              ),
              lang,
            )}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="anketa-public-shell">
      <div className="anketa-public-container anketa-page">
        {/* Логотип клиники */}
        <div className="anketa-public-brand">
          <img src={LogoText} alt="REFRESH Clinic" />
        </div>

        {/* Заголовок и переключатель языка */}
        <div className="block-title anketa-header">
          <div className="block-title-text">
            {pick(L("Анкета пациента", "Patient questionnaire"), lang)}
          </div>
          <div className="block-title-btn">
            <div
              className="anketa-lang-switch"
              role="group"
              aria-label="Language">
              <IoGlobeOutline className="anketa-lang-globe" />
              <button
                type="button"
                className={`anketa-lang-btn ${lang === "ru" ? "active" : ""}`}
                onClick={() => setLang("ru")}
                aria-pressed={lang === "ru"}>
                RU
              </button>
              <button
                type="button"
                className={`anketa-lang-btn ${lang === "en" ? "active" : ""}`}
                onClick={() => setLang("en")}
                aria-pressed={lang === "en"}>
                EN
              </button>
            </div>
          </div>
        </div>

        {/* Вводная информация */}
        <div className="anketa-intro">
          <div className="anketa-subtitle">
            {pick(
              L("Полный опросник пациента", "Complete patient questionnaire"),
              lang,
            )}
          </div>
          <p className="anketa-intro-text">
            {pick(
              L(
                "Пожалуйста, заполните анкету максимально подробно. Если вопрос к вам не относится, отметьте «нет» или «не применимо». Данные помогают врачу оценить состояние здоровья, риски и выбрать безопасный план обследования и лечения.",
                "Please fill in the questionnaire as fully as possible. If a question does not apply to you, mark 'no' or 'not applicable'. This information helps the doctor assess your health, risks, and choose a safe examination and treatment plan.",
              ),
              lang,
            )}
          </p>
          <p className="anketa-warning">
            {pick(
              L(
                "Важно: если сейчас есть сильная боль в груди, выраженная одышка, потеря сознания, внезапная слабость или онемение одной стороны тела, нарушение речи, массивное кровотечение, тяжелая аллергическая реакция либо мысли о причинении вреда себе — немедленно обратитесь за экстренной помощью.",
                "Important: if you currently have severe chest pain, marked shortness of breath, loss of consciousness, sudden weakness or numbness on one side of the body, speech difficulty, massive bleeding, a severe allergic reaction, or thoughts of harming yourself — seek emergency care immediately.",
              ),
              lang,
            )}
          </p>
        </div>

        {/* Разделы анкеты */}
        <div className="anketa-sections">
          {ANKETA_SECTIONS.map((section) => (
            <section className="anketa-section" key={section.id}>
              <h3 className="anketa-section-title">
                <span className="anketa-section-icon">{SECTION_ICONS[section.id]}</span>
                <span className="anketa-section-title-text">
                  <span className="anketa-section-num">{section.num}.</span>{" "}
                  {pick(section.title, lang)}
                </span>
              </h3>
              {section.note ? (
                <p className="anketa-section-note">
                  {pick(section.note, lang)}
                </p>
              ) : null}
              <div className="anketa-section-body">
                {section.fields.map((field) => (
                  <React.Fragment key={field.id}>
                    {renderField(field, lang, values, handlers)}
                  </React.Fragment>
                ))}
              </div>
            </section>
          ))}
        </div>

        {/* Ошибка */}
        {error ? <div className="anketa-public-error">{error}</div> : null}

        {/* Кнопка отправки */}
        <div className="anketa-public-submit">
          <button
            type="button"
            className="anketa-public-submit-btn"
            onClick={handleSubmit}
            disabled={submitting}>
            {submitting
              ? pick(L("Отправка…", "Sending…"), lang)
              : (
                <>
                  {pick(L("Отправить анкету", "Submit questionnaire"), lang)}
                  <IoPaperPlaneOutline className="anketa-submit-icon" />
                </>
              )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AnketaPublic;
